<template>
  <div class="card h-100 overflow-hidden">
    <div class="card-body pt-3 pb-0 pe-0">
      <div class="row me-2">
        <div class="col-12 d-flex justify-content-between">
          <div v-if="period" class="pt-2 text-lighter">
            <i class="fa fa-clock"></i><span class="ms-2 font-weight-bold">{{
              formatPeriod(period)
              }}</span>
          </div>

          <div class="form-group ms-3">
            <PlatoonDropdown id="ddPlatoon" :allow-all="isAdminRole" :selected="selectedPlatoon"
              @selected-platoon-id="(selectedValue) => (selectedPlatoon = selectedValue)" />

            <SectionDropdown style="margin-left: 1rem;" id="ddSectionFilterSleepRange" :selected="selectedSection"
              @selected-section-id="
                (selectedValue) => (selectedSection = selectedValue)
              " />
          </div>
        </div>
        <div class="col-lg-12 col-12">
          <div class="text-xs text-uppercase text-primary text-bold mb-1">
            {{ header }}
          </div>
          <span class="h5 mt-2 text-uppercase">{{
            getPlatoonName(selectedPlatoon) + " - " + getSectionName(selectedSection)
            }}</span>

          <div v-if="!hasData" class="text-muted py-3">
            No data found for the selected date range.
          </div>
          <div v-show="hasData" class="mt-4 chart">
            <canvas :id="id" class="chart-canvas"></canvas>
          </div>
        </div>

        <div class="col-lg-4 col-12">
          <div v-if="alerts && alerts.length > 0"
            class="d-flex flex-column mx-3 position-relative overflow-auto alert-panel mt-sm-4">
            <alert v-for="alert in alerts" :key="alert.message" :color="alert.color" :title="alert.section"
              :body="alert.message" :show-icon="false" />
          </div>
        </div>
        <div class="row bg-dark mt-4">
          <div class="col-12 pt-3">
            <div class="table-responsive">
              <el-table ref="dataTable" :data="dataSleep.rows" style="width: 100%" @sort-change="sortColumn"
                @row-click="handleSleepRowClick">
                <el-table-column prop="name" label="Name" width="240" sortable="custom">
                  <template #default="scope">
                    <span v-if="scope.row.user && scope.row.user.length > 0">
                      {{ scope.row.user[0].name }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column prop="section_name" label="Section" width="120" sortable="custom">
                  <template #default="scope">
                    <span v-if="scope.row.user && scope.row.user.length > 0">
                      <span class="dot me-2" :style="{
                        'background-color': getSectionColor(
                          scope.row.user[0].section_name
                        ),
                      }"></span>
                      {{ scope.row.user[0].section_name }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column prop="status_start_time" label="Bed Time" sortable="custom">
                  <template #default="scope">
                    {{ formatTimestamp(scope.row.status_start_time) }}
                  </template>
                </el-table-column>
                <el-table-column prop="status_end_time" label="Wake Time" sortable="custom">
                  <template #default="scope">
                    {{ formatTimestamp(scope.row.status_end_time) }}
                  </template>
                </el-table-column>
                <el-table-column prop="sleep_minutes" label="Hours of Sleep" sortable="custom">
                  <template #default="scope">
                    {{ getHoursOfSleep(scope.row.sleep_minutes) }}
                  </template>
                </el-table-column>
                <el-table-column prop="restorative_minutes" label="Restorative" sortable="custom">
                  <template #default="scope">
                    {{ getHoursOfSleep(scope.row.restorative_minutes) }}
                  </template>
                </el-table-column>
                <el-table-column prop="wake_events" label="Wake Events" width="96" sortable="custom">
                  <template #default="scope">
                    {{ scope.row.wake_events }}
                  </template>
                </el-table-column>
                <el-table-column prop="deep_sleep_part_count" label="Deep Sleep Continuity" sortable="custom">
                  <template #default="scope">
                    {{ scope.row.deep_sleep_part_count }} pts
                  </template>
                </el-table-column>
                <el-table-column prop="breathing_quality" label="Breathing Quality" width="120" sortable="custom">
                  <template #default="scope">
                    {{ scope.row.breathing_quality }} pts
                  </template>
                </el-table-column>

                <el-table-column prop="sleep_score" label="Score" sortable="custom">
                  <template #default="scope">
                    <argon-progress :color="getSleepColor(scope.row.sleep_score)" :percentage="scope.row.sleep_score"
                      :left-text="`${scope.row.sleep_score}%`" />
                  </template>
                </el-table-column>
                <el-table-column label="" width="60" prop="actions">
                  <template #default="scope">
                    <div class="d-flex">
                      <router-link target="_blank" :to="{
                        name: `User`,
                        params: { udid: scope.row.udid },
                        query: { date: getDate(scope.row.status_end_time) },
                      }" exact>
                        <icon-user-info />
                      </router-link>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer paginator">
      <div v-if="dataSleep && dataSleep.count > 0" class="d-flex justify-content-between align-items-center">
        <span class="ms-3 text-muted">{{ dataSleep.count }} record(s)</span>
        <app-pagination :records="dataSleep.count" :page-size="pageSize" :page="page" @update-page-selection="
          (selectedValue) => updatePageSelection(selectedValue)
        " />
      </div>
    </div>
  </div>

  <!-- Sleep details Modal -->
  <modal id="sleepDetailsModal" v-model:show="sleepDetailsModal" class="modal fade"
    footer-classes="justify-content-end align-items-center" :show-close="true"
    :modalContentClasses="'sleep-intense-model'">
    <template #header>
      <div class="header-container d-flex justify-content-between">
        <div class="header-text text-xl text-capitalize">Sleep Details</div>
        <div v-if="false" style="width:60%" id="sleep-navigation">
          <IconPreviousItem v-if="false" />
          <icon-user-info v-if="false"></icon-user-info>
          <span class="header-text text-xl text-capitalize">08 Apr 2025</span>
          <IconNextItem />
        </div>
      </div>
    </template>

    <div class="p-2">
      <SleepSummary v-if="this.selectedSleepRow" :selected-date="this.selectedSleepRow.bucket"
        :udid="this.selectedSleepRow.user[0].udid" :sleep-data="this.selectedSleepRow" />
    </div>

    <template #footer>
      <button type="button" class="btn btn-primary" @click="closeModal">DONE</button>
    </template>
  </modal>
</template>

<script>
const dayjs = require("dayjs");
import Utils from "@/helpers/utils";
import DateUtils from "@/helpers/dateUtils";

import Constants from "@/helpers/constants";
import ChartUtils from "@/helpers/chartUtils";
import Chart from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import Alert from "@/shared/Cards/Alert.vue";
import Modal from "@/shared/Modal.vue";
import SectionDropdown from "@/views/pages/components/SectionDropdown.vue";
import PlatoonDropdown from "@/views/pages/components/PlatoonDropdown.vue";
import SleepSummary from "./insights/sleep-details/SleepSummary.vue";

import ArgonProgress from "@/shared/Controls/ArgonProgress.vue";
import IconUserInfo from "@/shared/Controls/Icons/IconUserInfo.vue";
import IconPreviousItem from "@/shared/Controls/Icons/IconPreviousItem.vue";
import IconNextItem from "@/shared/Controls/Icons/IconNextItem.vue";


Chart.register(annotationPlugin);

export default {
  name: "SleepByDaySectionChart",
  components: {
    ArgonProgress,
    IconUserInfo,
    Alert,
    SectionDropdown,
    SleepSummary,
    Modal,
    IconPreviousItem,
    IconNextItem,
    PlatoonDropdown,
  },
  props: {
    id: {
      type: String,
      default: "sleep-by-day-chart",
    },
    period: {
      type: Array,
      required: true,
    },
    header: {
      type: String,
      default: "Sleep Chart",
    },
    title: {
      type: String,
      default: "Today",
    },
    selectedSectionId: {
      type: Number,
      default: -1,
    },
    selectedPlatoonId: {
      type: Number,
      default: -1,
    },
    height: {
      type: [String, Number],
      default: "200",
    },
    dataSleepRanges: {
      type: Array,
      required: true,
    },
    dataSleep: {
      type: Object,
      required: true,
    },
    chart: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
        color: String,
      },
    },
  },
  emits: [
    "selected-section-id",
    "selected-page",
    "selected-sort",
    "selected-order",
    "selected-platoon-id",
  ],
  data() {
    return {
      selectedSection: Constants.DEFAULT_SECTION,
      page: Constants.DEFAULT_PAGE,
      pageSize: Constants.DEFAULT_PAGE_SIZE,
      sections: [],
      alerts: [],
      annotationsAvg: [],
      sectionColors: [],
      selectedSleepRow: null,
      sleepDetailsModal: false,
      selectedPlatoon: Constants.DEFAULT_PLATOON,
      isAdminRole: false,
    };
  },
  mounted() {
    let loggedInUser = this.$store.getters["auth/getLoggedInUser"]();
    if (loggedInUser.role && loggedInUser.role.role_id == Constants.ROLES.PLATOON_COMMANDER) {
      this.selectedPlatoon = loggedInUser.platoon_id;
    }
    if (Constants.ADMIN_USER_ROLES.includes(loggedInUser.role.role_id)) {
      this.isAdminRole = true;
    }
  },
  computed: {
    hasData() {
      return this.dataSleepRanges && this.dataSleepRanges.length > 0;
    },
  },
  watch: {
    chart() {
      if (this.dataSleepRanges && this.dataSleepRanges.length > 0)
        this.addChart();
      // this.updateAlerts();
    },
    selectedSection() {
      // for filter
      this.$emit("selected-section-id", this.selectedSection);
    },
    selectedSectionId() {
      this.selectedSection = this.selectedSectionId;
    },
    selectedPlatoon() {
      // for filter
      this.$emit("selected-platoon-id", this.selectedPlatoon);
    },
    selectedPlatoonId() {
      this.selectedPlatoon = this.selectedPlatoonId;
    },
  },
  methods: {
    sortColumn({ prop, order }) {
      this.$emit("selected-sort", prop);
      this.$emit("selected-order", order == "ascending" ? "asc" : "desc");
    },
    formatPeriod(period) {
      return Utils.formatPeriod(period);
    },
    formatDate(day) {
      return dayjs(day).format(Constants.TABLE_DATE_FORMAT);
    },
    formatTimestamp(ts) {
      let timestamp = parseInt(ts);
      return dayjs(timestamp).format(Constants.TABLE_DATETIME_FORMAT);
    },
    formatTime(ts) {
      return dayjs(ts).format(Constants.TABLE_TIME_FORMAT);
    },
    getSleepColor(score) {
      return Utils.getSleepScoreColor(score);
    },
    getSectionColor(sectionName) {
      return Utils.stringToHexColor(sectionName.toUpperCase());
    },
    getHoursOfSleep(statusMinutes) {
      let formatted = dayjs
        .duration(statusMinutes * 60 * 1000)
        .format(Constants.TABLE_HOURS_MINUTES_FORMAT);
      return formatted;
    },
    getSectionName(section_id) {
      const section = this.$store.getters["list/getSectionName"](section_id);
      return section ? section.name : "";
    },
    getPlatoonName(platoon_id) {
      const platoon = this.$store.getters["list/getPlatoonName"](platoon_id);
      return platoon ? platoon.name : "";
    },
    addChart() {
      this.sections = [];
      this.annotationsAvg = [];
      const ctx = document.getElementById(this.id).getContext("2d");

      let chartStatus = Chart.getChart(this.id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }

      let chart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: this.chart.labels,
          datasets: [],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            annotation: {
              annotations: this.annotationsAvg || [],
            },
            legend: {
              position: "bottom",
              labels: {
                color: "#fff",
                boxWidth: 12,
                boxHeight: 12,
                padding: 40,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            x: {
              ticks: {
                color: "#95a7a3",
              },
            },
            y: {
              display: true,
              beginAtZero: true,
              position: "left",
              min: 0,
              max: 100,
              ticks: {
                stepSize: 10,
                color: "#95a7a3",
              },
              title: {
                display: true,
                text: "SLEEP SCORE",
                color: "#fff",
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: true, // only want the grid lines for one axis to show up
              },
            },
          },
        },
      });

      // add dataset
      this.chart.datasets.forEach((data, index) => {
        const randomColor =
          this.selectedSection != -1
            ? ChartUtils.sectionColor(
              this.getSectionName(this.selectedSection).toUpperCase()
            )
            : Utils.stringToHexColor(
              this.getSectionName(index + 1).toUpperCase()
            );

        this.sections.push({
          index: index,
          name: data.label,
          color: randomColor,
        });
        this.annotationsAvg.push({
          id: "avgLine" + index,
          type: "line",
          borderColor: randomColor,
          borderDash: [3, 3],
          borderDashOffset: 0,
          borderWidth: 1,
          label: {
            display: true,
            content: "",
            position: "start",
          },
          scaleID: "y",
          value: this.dataSleepRanges[index].average,
        });

        chart.data.datasets.push({
          categoryPercentage: 0.3, // keeps bars closer
          label: data.label,
          data: data.data,
          backgroundColor: randomColor,
          tension: 0.4,
          borderWidth: 1,
          borderRadius: 4,
          borderColor: randomColor,
          borderSkipped: false,
          maxBarThickness: 8,
        });
      });
      chart.update();
    },
    updateAlerts() {
      if (this.dataSleepRanges && this.dataSleepRanges.length > 0) {
        this.dataSleepRanges.map((record) => {
          record.messages.map((msg) => {
            this.alerts.push({
              section: record.section,
              color: this.getSectionColor(record.section),
              message: msg,
            });
          });
        });
      }
    },
    updatePageSelection(selectedPage) {
      this.page = selectedPage;
      this.$emit("selected-page", selectedPage);
    },
    getDate(dateTime) {
      let timestamp = parseInt(dateTime);
      return DateUtils.formatDateToBucketDate(timestamp);
    },
    handleSleepRowClick(sleepRow, column) {
      if (column?.property === 'actions') return;

      this.selectedSleepRow = null;
      this.sleepDetailsModal = !this.sleepDetailsModal;
      this.selectedSleepRow = sleepRow;
    },
    closeModal() {
      this.selectedWorkoutRow = null;
      this.sleepDetailsModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.dot {
  height: 8px;
  width: 8px;
  background-color: #fff;
  border-radius: 50%;
  display: inline-block;
}

@media (max-width: 575.98px) {
  .alert-panel {
    height: auto;
    margin-top: 1rem;
  }
}
</style>
